/**
 * What staff can do while a live route is still pricing and nothing has been chosen (Ben, 20 Oct 2026): wait for the
 * route (the page keeps asking and the order buttons come back once every route has answered), or use the best route
 * priced so far now. The page decides what "now" commits; this only offers the two.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import type { PendingPriceRoute } from "@shared-core/data/supplier-pricing-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import {
  stillPricingSentence,
  bestPricedNowLabel,
  waitForRoutesLabel,
  waitingForRoutesLine,
  type BestPricedRoute,
} from "@shared-core/domain/price-still-coming";
import { Button } from "../primitives/button";

export function PendingRouteChoice({
  pending,
  best,
  onUseBest,
  onWait,
}: {
  pending: readonly PendingPriceRoute[];
  /** The best route priced so far; null when none is (the page can only wait). */
  best: Pick<BestPricedRoute, "routeLabel" | "pricePence"> | null;
  onUseBest: () => void;
  /** Told when the person chooses to wait, for a screen that asks again only once they have. */
  onWait?: () => void;
}) {
  const [isWaiting, setIsWaiting] = useState(false);
  return (
    <div
      role="status"
      data-testid="pending-route-choice"
      className="space-y-2 rounded-md border border-border bg-muted/40 p-3"
    >
      <p className="text-sm">{stillPricingSentence(pending)}</p>
      <div className="flex flex-wrap items-center gap-2">
        {isWaiting ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {waitingForRoutesLine(pending)}
          </span>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setIsWaiting(true);
              onWait?.();
            }}
          >
            {waitForRoutesLabel(pending)}
          </Button>
        )}
        {best && (
          <Button type="button" size="sm" onClick={onUseBest}>
            {bestPricedNowLabel(best, formatQuotedPrice)}
          </Button>
        )}
      </div>
    </div>
  );
}
